import { format } from "timeago.js";
import { ColumnsType } from "antd/es/table";
import { App, Switch } from "antd";
import { AlertsOff, AlertsOn, ExploreIcon } from "./Icons";
import styles from "./SavedViewWithMonitoringPage.module.scss";
import Moment from "react-moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import VLTable from "../../../uikit/VLTable";
import { Query, SavedView } from "../../../../types";
import { RightOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { useVisualSimilarityImages } from "../../../../hooks/useVisualSimilarityImages";
import useVqlParams from "../../../../hooks/useVqlParams";
import { QueryPanelRow } from "../../../components/QueryPanel";
import VLButton from "../../../uikit/Buttons/VLButton";
import useNavigateToView from "../../../../hooks/useNavigateToView";
import { VLTableActionsConfig } from "../../../uikit/VLTable/tableUtils";
import ShareIcon from "../../../../assets/icons/ShareIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import { DeleteViewModal } from "../DeleteViewModal";
import { setShowViewSharingMenu } from "../../../../redux/SingleDataset/actions";
import { useDispatch } from "react-redux";
import VLAvatar from "../../../uikit/VLAvatar";
import dayjs from "dayjs";
import {
  ColorType,
  createChart,
  CrosshairMode,
  HistogramSeries,
} from "lightweight-charts";
import { formatNumberWithCommas } from "../../../../helpers/utility/formatters";
import ContentBodyLoader from "../../../components/MetadataSummary/Content/ContentBody/ContentBodyLoader";
import { fetchSetViewAlertingApiV1DatasetDatasetIdViewsViewIdAlertingPost } from "../../../../vl-api/vlComponents";
import {
  mergePreviewVql,
  useSavedViewPreviewAndMonitoring,
} from "./useSavedViewPreviewAndMonitoring";
import { useSearchParams } from "react-router-dom";

const createColumns = (datasetId: string): ColumnsType<any> => [
  {
    key: "name",
    title: "Name",
    dataIndex: "name",
    render: (name) => <div className={styles.viewNameValue}>{name}</div>,
    className: styles.viewName,
  },
  {
    key: "totals",
    title: "Total Results",
    className: styles.columnTextSecondary,
    render: (_, view: SavedView) =>
      (view.stats.n_objects || view.stats.n_images) + (view.totalResults ?? 0),
  },
  {
    key: "lastResult",
    title: "Last Result",
    className: styles.columnTextSecondary,
    dataIndex: "createdAt",
    render: (createdAt: string) => format(new Date(createdAt + "Z")),
  },
  {
    key: "createdAt",
    title: "Creation Time",
    className: styles.columnTextSecondary,
    dataIndex: "createdAt",
    render: (createdAt) => (
      <Moment format="M/D/YY, HH:mm" local utc>
        {createdAt}
      </Moment>
    ),
  },
  {
    key: "createdBy",
    title: "Created By",
    dataIndex: "createdBy",
    render: (user: SavedView["createdBy"]) => (
      <>
        <VLAvatar size="small" src={user.avatar_uri} />
        <span className={styles.userName}>{user.name}</span>
      </>
    ),
  },
  {
    key: "monitoring",
    title: "Monitoring",
    render: (_, view) => <AlertingSwitch datasetId={datasetId} view={view} />,
  },
];

const AlertingSwitch = ({
  view,
  datasetId,
}: {
  datasetId: string;
  view: SavedView;
}) => {
  const {
    modal: { error },
  } = App.useApp();
  const [enabled, setEnabled] = useState(view.alertingEnabled);
  const handleChange = async (value: boolean) => {
    try {
      setEnabled(value);
      await fetchSetViewAlertingApiV1DatasetDatasetIdViewsViewIdAlertingPost({
        pathParams: { datasetId, viewId: view.id },
        body: { request: { enabled: value } },
      });
    } catch (e: any) {
      setEnabled(enabled);
      error({ title: "Operation failed", content: e.detail });
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Switch
        checked={enabled}
        onChange={handleChange}
        checkedChildren={<AlertsOn />}
        unCheckedChildren={<AlertsOff />}
      />
    </div>
  );
};

export const SavedViewsTable = ({
  views,
  datasetId,
}: {
  datasetId: string;
  views: SavedView[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewForDeletion, setViewForDeletion] = useState<SavedView | null>(
    null,
  );
  const [expandedRowsIds, setExpandedRowsIds] = useState<string[]>([]);
  useEffect(() => {
    const nameFromQuery = searchParams.get("name");
    if (nameFromQuery) {
      const view = views.find((view) => view.name === nameFromQuery);
      if (view) {
        const rowElement = document.querySelector(
          `tr[data-row-key="${view.id}"]`,
        ) as HTMLElement;
        if (rowElement) {
          rowElement.click();
          rowElement.scrollIntoView({ behavior: "smooth" });
        }
      }
      searchParams.delete("name");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, views]);
  const dispatch = useDispatch();
  const actionsConfig = useMemo<VLTableActionsConfig<SavedView>>(
    () => ({
      items: (view) => [
        {
          key: "share",
          label: "Share",
          onClick: () =>
            dispatch(
              setShowViewSharingMenu({
                showSharingMenu: true,
                associatedViewId: view.id,
              }),
            ),
          icon: <ShareIcon />,
          className: styles.actionItems,
        },
        {
          key: "delete",
          label: "Delete",
          onClick: () => setViewForDeletion(view),
          icon: <DeleteIcon className={styles.deleteIcon} />,
          className: styles.actionItems,
        },
      ],
    }),
    [dispatch],
  );
  const handleRowExpand = (id: string, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedRowsIds((prev) => [...prev, id]);
    } else {
      setExpandedRowsIds((prev) => prev.filter((i) => i !== id));
    }
  };
  return (
    <>
      <VLTable
        pagination={{ total: views.length, tooltip: "" }}
        className={styles.table}
        columns={createColumns(datasetId)}
        dataSource={views}
        rowKey="id"
        actions={actionsConfig}
        rowClassName={({ id }) =>
          expandedRowsIds.includes(id) ? styles.expandedRow : ""
        }
        expandable={{
          onExpand: (expanded, record) => handleRowExpand(record.id, expanded),
          expandRowByClick: true,
          expandedRowRender: (view: any) => (
            <ViewDetails
              view={view}
              datasetId={datasetId}
              expanded={expandedRowsIds.includes(view.id)}
            />
          ),
          expandIcon: ({ expanded, onExpand, record }) => (
            <RightOutlined
              className={classNames({ [styles.expandedColumn]: expanded })}
              onClick={(e) => onExpand(record, e)}
            />
          ),
        }}
      />
      {viewForDeletion && (
        <DeleteViewModal
          view={viewForDeletion}
          onCloseModal={() => setViewForDeletion(null)}
        />
      )}
    </>
  );
};

const formatDate = (date: string | number) =>
  dayjs(date).format("M/D/YY, HH:mm");

const ViewDetails = ({
  view,
  datasetId,
  expanded,
}: {
  datasetId: string;
  view: SavedView;
  expanded: boolean;
}) => {
  const { vqlToQuery } = useVqlParams();
  const { imageDataMap } = useVisualSimilarityImages({
    vqlQueries: view.filters.vql!,
    datasetId,
  });
  const [selectedTime] = useState<string | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const queries: Query[] = useMemo(() => {
    return view.filters
      .vql!.map((vqlQuery) => vqlToQuery(vqlQuery, imageDataMap))
      .filter((query): query is Query => query !== null);
  }, [view.filters, vqlToQuery, imageDataMap]);
  const navigateToView = useNavigateToView();
  const {
    imageUrls,
    trendPoints,
    isLoading,
    setPreviewVql,
    previewVql,
    isPreviewLoading,
  } = useSavedViewPreviewAndMonitoring(datasetId, view);

  useEffect(() => {
    if (!expanded && previewVql != null) {
      setPreviewVql(null);
    }
  }, [expanded, previewVql, setPreviewVql]);

  const showTrendChart = trendPoints?.length > 1;

  const handleNavigateToView = useCallback(() => {
    const viewToNavigate =
      previewVql != null
        ? {
            ...view,
            filters: {
              ...view.filters,
              vql: mergePreviewVql(view, previewVql),
            },
          }
        : view;
    navigateToView(viewToNavigate);
  }, [view, previewVql, navigateToView]);

  useEffect(() => {
    if (isLoading || !chartContainerRef.current || !trendPoints.length) {
      return;
    }

    const getColor = (name: `--${string}`) =>
      getComputedStyle(document.body).getPropertyValue(name);

    const gridColor = getColor("--new-neutral-900");
    const textColor = getColor("--new-text-on-surface-400");

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth - 16,
      height: 260,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: textColor,
        fontFamily: "Inter",
      },
      grid: {
        vertLines: { visible: false, color: gridColor },
        horzLines: {
          visible: true,
          color: gridColor,
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: {
          visible: false,
          labelVisible: false,
        },
        horzLine: {
          visible: false,
          labelVisible: false,
        },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true,
      },
      leftPriceScale: {
        visible: true,
        borderVisible: false,
        autoScale: false,
        entireTextOnly: true,
      },
      rightPriceScale: {
        visible: false,
      },
    });

    const histogramSeries = chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: "volume",
      },
      priceScaleId: "left",
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const chartData = trendPoints.map(({ timestamp, timestampRaw, count }) => ({
      time: Math.floor(new Date(timestamp + "Z").getTime() / 1000) as any,
      timestamp,
      timestampRaw: timestampRaw,
      value: count,
      color:
        selectedTime === timestamp
          ? "#0197D8"
          : getColor("--new-metadata-light-blue-100"),
    }));

    histogramSeries.setData(chartData);

    // Add spacing between bars by expanding the visible range
    if (chartData.length > 0) {
      const firstTime = chartData[0].time as number;
      const lastTime = chartData[chartData.length - 1].time as number;
      const oneDayInSeconds = 86400;

      // Calculate spacing - add padding to create gaps between bars
      const dataRange = lastTime - firstTime || oneDayInSeconds;
      const paddingPercentage = 0.15; // 15% padding on each side
      const padding = dataRange * paddingPercentage;

      chart.timeScale().setVisibleRange({
        from: (firstTime - padding) as any,
        to: (lastTime + padding) as any,
      });

      chart.timeScale().applyOptions({
        barSpacing: 10,
      });
    } else {
      chart.timeScale().fitContent();
    }

    // Create tooltip element
    const tooltip = document.createElement("div");
    tooltip.className = styles.chartTooltip;
    chartContainerRef.current.appendChild(tooltip);

    // Subscribe to crosshair move
    chart.subscribeCrosshairMove((param) => {
      if (
        !param.time ||
        !param.point ||
        param.point.x < 0 ||
        param.point.y < 0
      ) {
        tooltip.style.display = "none";
        if (chartContainerRef.current) {
          chartContainerRef.current.style.cursor = "default";
        }
        return;
      }

      const data = param.seriesData.get(histogramSeries) as any;
      // Get the price coordinate for the data value
      const priceCoordinate = histogramSeries.priceToCoordinate(data?.value);
      if (priceCoordinate === null) {
        tooltip.style.display = "none";
        if (chartContainerRef.current) {
          chartContainerRef.current.style.cursor = "default";
        }
        return;
      }

      // Only show tooltip if cursor is at or below the top of the bar
      // (bars are drawn from bottom up, so cursor.y should be >= bar top)
      if (param.point.y < priceCoordinate) {
        tooltip.style.display = "none";
        if (chartContainerRef.current) {
          chartContainerRef.current.style.cursor = "default";
        }
        return;
      }

      const timestamp = chartData.find(
        ({ time }) => time === data.time,
      )!.timestamp;
      const value = data.value;

      tooltip.style.display = "block";
      tooltip.innerHTML = `
        <strong>${formatDate(timestamp)}</strong>
        <div class="${styles.value}">
          Value: ${formatNumberWithCommas(value)}
        </div>
      `;

      const tooltipWidth = 150;
      const tooltipHeight = 60;
      const chartWidth = chartContainerRef.current!.clientWidth;

      let left = param.point.x - tooltipWidth / 2;
      if (left < 0) {
        left = 0;
      }
      if (left + tooltipWidth > chartWidth) {
        left = chartWidth;
      }

      // Position tooltip right above the bar top
      let top = priceCoordinate + tooltipHeight;

      // If tooltip go above chart, position it below the bar
      if (top < 0) {
        top = priceCoordinate + 8;
      }

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;

      if (chartContainerRef.current) {
        chartContainerRef.current.style.cursor = "pointer";
      }
    });

    chart.subscribeClick((param) => {
      if (!param.time || !param.point || !param.seriesData) {
        return;
      }
      const data = param.seriesData.get(histogramSeries) as
        | { time: number; value: number }
        | undefined;
      if (!data) {
        return;
      }
      const point = chartData.find((d) => d.time === data.time);
      if (!point?.timestampRaw) {
        return;
      }
      setPreviewVql({
        id: view.id,
        created_at: { op: "lte", value: point.timestampRaw },
      });
    });

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth - 16,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
      chart.remove();
    };
  }, [trendPoints, selectedTime, isLoading, view.id, datasetId, setPreviewVql]);
  return (
    <div className={styles.viewDetails}>
      <div className={styles.section}>
        <div className={styles.inner}>
          <QueryPanelRow
            key="andRow"
            queries={queries}
            onRemove={() => null}
            onClick={() => null}
            onReset={() => null}
            interactive={false}
          />
          <VLButton
            onClick={handleNavigateToView}
            className={styles.exploreButton}
            type="secondary"
            size="small"
            icon={<ExploreIcon />}
          >
            Explore Results
          </VLButton>
        </div>
      </div>
      {isLoading ? (
        <ContentBodyLoader center size={64} />
      ) : (
        <div className={styles.row}>
          {showTrendChart && (
            <div className={classNames(styles.section, styles.trend)}>
              <div className={styles.header}>
                <div>Trend Results</div>
                <div>
                  Total Results:{" "}
                  {formatNumberWithCommas(
                    selectedTime
                      ? trendPoints.find((i) => i.timestamp === selectedTime)!
                          .count
                      : view.stats.n_images || view.stats.n_objects,
                  )}
                </div>
              </div>
              <div ref={chartContainerRef} className={styles.chartContainer} />
            </div>
          )}
          <div
            className={classNames(styles.section, styles.preview, {
              [styles.fullWidth]: !showTrendChart,
            })}
          >
            {isPreviewLoading ? (
              <ContentBodyLoader center size={64} />
            ) : (
              <Previews imageUrls={imageUrls!} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const IMAGE_WIDTH = 150;

const Previews = ({ imageUrls }: { imageUrls: string[] }) => {
  const [columnCount, setColumnCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      const width = ref.current.clientWidth;
      setColumnCount(Math.floor(width / IMAGE_WIDTH));
    }
  }, []);
  return (
    <div className={styles.previewsWrapper}>
      <div className={styles.previews} ref={ref} style={{ columnCount }}>
        {Boolean(columnCount) &&
          imageUrls!.map((src) => (
            <img className={styles.previewItem} src={src} alt="" />
          ))}
      </div>
    </div>
  );
};
